/**
 * Intervalo (horas) e tempo (dias) do medicamento no protocolo.
 *
 * O autocomplete às vezes devolve só `{ label }` ou um texto livre.
 * O save antigo lia apenas `.value` e, no update, apagava os dois campos.
 */

export type ProtocolTimingKind = "hours" | "days";

export type ProtocolTimingOption = {
  value: number | "";
  label: string;
  name: string;
};

function leadingNumber(text: string): string | undefined {
  return text.trim().match(/^(\d+)/)?.[1];
}

function timingLabel(amount: string, kind: ProtocolTimingKind): string {
  if (kind === "hours") return `${amount} h`;
  return amount === "1" ? "1 dia" : `${amount} dias`;
}

/** Normaliza opção do formulário, string do autocomplete ou texto vindo do FHIR. */
export function normalizeProtocolTiming(
  raw: unknown,
  kind: ProtocolTimingKind,
): ProtocolTimingOption | undefined {
  if (raw == null || raw === "") return undefined;

  const text =
    typeof raw === "object"
      ? String(
          (raw as { label?: unknown; name?: unknown; value?: unknown }).label ||
            (raw as { name?: unknown }).name ||
            (raw as { value?: unknown }).value ||
            "",
        ).trim()
      : String(raw).trim();

  if (!text) return undefined;

  const amount = leadingNumber(text);
  if (!amount) {
    // Ex.: "tempo indeterminado" — não tem número, mas precisa continuar salvo.
    return { value: "", label: text, name: text };
  }

  const label = timingLabel(amount, kind);
  return { value: Number(amount), label, name: label };
}

/** Valor string aceito em QuestionnaireResponse.answer.valueString. */
export function protocolTimingValueString(raw: unknown): string | undefined {
  const option = normalizeProtocolTiming(raw, "hours");
  if (!option) return undefined;
  if (option.value === "") return option.label;
  return String(option.value);
}
